import React, { useContext, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';

import { selectUser, getMeStatus } from '../../reducers/user.selectors';
import { AsyncActionStatus } from '../../actions/common.actionTypes';
import useScreenSize from '../../utils/useScreenSize';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import SubscriptionInfo from '../../components/SubscriptionInfo';
import PaymentInfo from '../../components/PaymentInfo';
import BillingAddressInfo from '../../components/BillingAddressInfo';
import InvoiceHistory from '../../components/InvoiceHistory';
import ChangeEmailSection from '../../components/ChangeEmailSection';
import ChangePasswordSection from '../../components/ChangePasswordSection';
import NotificationAlert from '../../components/NotificationAlert';
import { NotificationContext } from '../../state/NotificationContextProvider';

const AccountPage = () => {
  const { isXsScreen, isSmScreen, isMdScreen, isLgScreen } = useScreenSize();
  const { notification, hideNotification } = useContext(NotificationContext);
  const user = useSelector(selectUser);
  const meStatus = useSelector(getMeStatus);

  // On a hard reload `me` is not in the store yet, so `isStripeUser` would
  // momentarily be false and flash the non-Stripe layout before the user data
  // arrives. Gate the subscription-dependent sections until `me` is resolved.
  const isUserLoading = !user && meStatus === AsyncActionStatus.LOADING;

  // Stripe users manage payment, billing address and invoices in the Stripe
  // billing portal, so those sections are hidden here (see SubscriptionInfo).
  const isStripeUser = !!user?.isStripeUser;

  const memoizedNotification = useMemo(() => {
    if (!notification) return null;
    
    return (
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
        <Box sx={{ position: 'relative' }}>
          <NotificationAlert
            message={notification.message}
            severity={notification.severity}
            description={notification?.description}
            onClose={hideNotification}
            alertSx={{ width: '100%' }}
          />
        </Box>
      </Box>
    );
  }, [notification, hideNotification]);

  const getPaddingX = () => {
    if (isLgScreen) {
      return spaces.xl;
    } else if (isMdScreen || isSmScreen) {
      return spaces.lg;
    } else {
      return spaces.md;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'fit-content',
        gap: spaces.md,
        backgroundColor: palette.background.paperElevation1,
        paddingX: getPaddingX(),
        paddingBottom: isXsScreen ? spaces.md : spaces.xxxxxl,
      }}
    >
      {memoizedNotification}
      <ChangeEmailSection />
      <ChangePasswordSection />
      {isUserLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: spaces.xxxxxl }}>
          {/* Force the inner svg to fill the root box; MUI leaves it unsized, so
              it renders smaller than the span and looks off-center. */}
          <CircularProgress sx={{ '& .MuiCircularProgress-svg': { width: '100%', height: '100%' } }} />
        </Box>
      ) : (
        <>
          <SubscriptionInfo />
          {!isStripeUser && <PaymentInfo />}
          {!isStripeUser && <BillingAddressInfo />}
          {!isStripeUser && <InvoiceHistory />}
        </>
      )}
    </Box>
  );
};

export default AccountPage;
